import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Stack } from '@osd/ui/stack';
import { Text } from '@osd/ui/text';
import { Table } from '@osd/ui/table';
import type { TableSortDirection } from '@osd/ui/table';
import { CodeBlock } from '@osd/ui/code-block';
import { Section } from '@osd/ui/section';

/**
 * /ui/table — live docs for the `Table` primitive.
 *
 * Table is the compound data-table primitive (root/osd#98):
 * real `<table>` / `<thead>` / `<tbody>` / `<tr>` / `<th>` / `<td>`
 * for assistive tech + copy/paste + print, with `display: grid`
 * layered on top so a single `columns` template lines every row up.
 *
 * Compound parts:
 *   - `<Table>`              — root, takes `columns` + optional `caption`
 *   - `<Table.Header>`       — `<thead>` row-group
 *   - `<Table.Body>`         — `<tbody>` row-group
 *   - `<Table.Row>`          — `<tr>` row
 *   - `<Table.Head>`         — `<th scope="col">` column header
 *   - `<Table.SortableHead>` — `<th>` with sort button + `aria-sort`
 *   - `<Table.Cell>`         — `<td>`
 *
 * Sort state is owned by the caller — `Table.SortableHead` exposes
 * `direction` + `onSort` and the parent decides what to do. Zebra
 * striping is automatic (`:nth-child(even)` on body rows). Below the
 * `md` breakpoint the SCSS collapses to stacked cards using each
 * cell's `data-label` as a heading.
 */

interface TeamRow {
  readonly id: number;
  readonly name: string;
  readonly role: string;
  readonly points: number;
}

type SortKey = 'name' | 'role' | 'points';

interface SortState {
  readonly key: SortKey;
  readonly direction: TableSortDirection;
}

const TEAMS: readonly TeamRow[] = [
  { id: 1, name: 'Aurora', role: 'Admin', points: 142 },
  { id: 2, name: 'Beacon', role: 'Editor', points: 87 },
  { id: 3, name: 'Comet', role: 'Viewer', points: 54 },
  { id: 4, name: 'Drift', role: 'Editor', points: 96 },
  { id: 5, name: 'Eclipse', role: 'Admin', points: 120 },
];

export const Route = createFileRoute('/ui/_layout/table')({
  component: TablePage,
});

function TablePage(): JSX.Element {
  const [sort, setSort] = useState<SortState>({
    key: 'points',
    direction: 'descending',
  });

  const sorted = [...TEAMS].sort((a, b) => {
    const dir = sort.direction === 'ascending' ? 1 : -1;
    if (sort.key === 'name') return a.name.localeCompare(b.name) * dir;
    if (sort.key === 'role') return a.role.localeCompare(b.role) * dir;
    return (a.points - b.points) * dir;
  });

  function handleSort(key: SortKey): void {
    setSort((current) => {
      if (current.key !== key) {
        return { key, direction: 'ascending' };
      }
      const nextDirection: TableSortDirection =
        current.direction === 'ascending' ? 'descending' : 'ascending';
      return { key, direction: nextDirection };
    });
  }

  function directionFor(key: SortKey): TableSortDirection {
    return sort.key === key ? sort.direction : 'none';
  }

  return (
    <Stack gap={6}>
      <Section
        title="Table"
        hint="Compound data-table primitive — real <table> + display: grid layout"
      >
        <Table columns="2fr 1fr 1fr" caption="Workspace members (static)">
          <Table.Header>
            <Table.Row>
              <Table.Head>Team</Table.Head>
              <Table.Head>Role</Table.Head>
              <Table.Head>Points</Table.Head>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {TEAMS.map((team) => (
              <Table.Row key={team.id}>
                <Table.Cell>{team.name}</Table.Cell>
                <Table.Cell>{team.role}</Table.Cell>
                <Table.Cell>{team.points}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Section>

      <Section
        title="Sortable heads"
        hint="<Table.SortableHead direction onSort> — parent owns the sort state"
      >
        <Stack gap={3}>
          <Text>
            Active sort:{' '}
            <strong>
              {sort.key} ({sort.direction})
            </strong>
          </Text>
          <Table columns="2fr 1fr 1fr">
            <Table.Header>
              <Table.Row>
                <Table.SortableHead
                  direction={directionFor('name')}
                  onSort={() => handleSort('name')}
                >
                  Team
                </Table.SortableHead>
                <Table.SortableHead
                  direction={directionFor('role')}
                  onSort={() => handleSort('role')}
                >
                  Role
                </Table.SortableHead>
                <Table.SortableHead
                  direction={directionFor('points')}
                  onSort={() => handleSort('points')}
                >
                  Points
                </Table.SortableHead>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {sorted.map((team) => (
                <Table.Row key={team.id}>
                  <Table.Cell>{team.name}</Table.Cell>
                  <Table.Cell>{team.role}</Table.Cell>
                  <Table.Cell>{team.points}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </Stack>
      </Section>

      <Section
        title="Caption + accessibility"
        hint="caption is rendered inside <caption>; every head uses scope='col'"
      >
        <Table columns="2fr 1fr" caption="First three members">
          <Table.Header>
            <Table.Row>
              <Table.Head>Name</Table.Head>
              <Table.Head>Role</Table.Head>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {TEAMS.slice(0, 3).map((team) => (
              <Table.Row key={team.id}>
                <Table.Cell>{team.name}</Table.Cell>
                <Table.Cell>{team.role}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Section>

      <Section title="Code example" hint="Copy-pasteable usage">
        <Stack gap={5}>
          <CodeBlock
            title="Compound table"
            language="tsx"
            code={`<Table columns="2fr 1fr 1fr" caption="Workspace members">
  <Table.Header>
    <Table.Row>
      <Table.Head>Name</Table.Head>
      <Table.Head>Role</Table.Head>
      <Table.Head>Points</Table.Head>
    </Table.Row>
  </Table.Header>
  <Table.Body>
    {rows.map((row) => (
      <Table.Row key={row.id}>
        <Table.Cell>{row.name}</Table.Cell>
        <Table.Cell>{row.role}</Table.Cell>
        <Table.Cell>{row.points}</Table.Cell>
      </Table.Row>
    ))}
  </Table.Body>
</Table>`}
          />
          <CodeBlock
            title="Sortable variant"
            language="tsx"
            code={`const [sort, setSort] = useState<SortState>({
  key: 'name',
  direction: 'ascending',
});

<Table columns="2fr 1fr">
  <Table.Header>
    <Table.Row>
      <Table.SortableHead
        direction={sort.key === 'name' ? sort.direction : 'none'}
        onSort={() => setSort(toggleSort('name'))}
      >
        Name
      </Table.SortableHead>
    </Table.Row>
  </Table.Header>
  <Table.Body>…</Table.Body>
</Table>`}
          />
        </Stack>
      </Section>
    </Stack>
  );
}
